import { useId, useState } from 'react';
import { TimesheetIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { TextField } from '@/components/ui/TextField';
import type { ReferenceRef } from '@/features/portal/types';
import type { Timesheet, TimesheetOvertime, TimesheetOvertimeInput } from '../types';
import { missionMonthBounds, overtimeInput } from './timesheet-extras-utils';
import { overtimeDuration, validateOvertime } from './overtime-validation';

export const TimesheetOvertimeModal = ({
  timesheet,
  entry,
  types,
  pending,
  onSave,
  onClose,
}: {
  timesheet: Timesheet;
  entry: TimesheetOvertime | null;
  types: ReferenceRef[];
  pending: boolean;
  onSave: (entries: TimesheetOvertimeInput[]) => Promise<void>;
  onClose: () => void;
}) => {
  const id = useId();
  const bounds = missionMonthBounds(timesheet);
  const [draft, setDraft] = useState<TimesheetOvertimeInput>(
    entry
      ? overtimeInput(entry)
      : {
          date: bounds.from,
          startTime: '18:00',
          endTime: '20:00',
          typeId: null,
          surchargeRate: null,
          motive: '',
        },
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const busy = pending || saving;
  const duration = overtimeDuration(draft.startTime, draft.endTime);
  const submit = async () => {
    const others = timesheet.overtimes.filter((item) => item.id !== entry?.id).map(overtimeInput);
    const validation = validateOvertime(draft, others, bounds);
    if (validation) {
      setError(validation);
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave(
        [...others, draft].sort(
          (a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime),
        ),
      );
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Impossible d’enregistrer ces heures. Réessayez.',
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      title={entry ? 'Modifier les heures supplémentaires' : 'Ajouter des heures supplémentaires'}
      description={timesheet.mission.title || timesheet.mission.projectName}
      icon={<TimesheetIcon />}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <div className="cra-extra-form__footer">
          <Button variant="secondary" size="sm" disabled={busy} onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            form={id}
            size="sm"
            loading={busy}
            disabled={busy}
            loadingLabel="Enregistrement…"
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <form
        id={id}
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) void submit();
        }}
      >
        <fieldset className="cra-extra-form" disabled={busy}>
          <legend className="sr-only">Heures supplémentaires</legend>
          <div className="cra-extra-form__row">
            <DatePicker
              label="Date"
              required
              value={draft.date}
              min={bounds.from}
              max={bounds.to}
              onValueChange={(date) => setDraft({ ...draft, date })}
            />
            <Select
              label="Type"
              placeholder="Non précisé"
              options={types.map((type) => ({ value: type.id, label: type.label }))}
              value={draft.typeId ?? ''}
              onChange={(event) => setDraft({ ...draft, typeId: event.target.value || null })}
            />
          </div>
          <div className="cra-extra-form__row">
            <TextField
              label="Heure de début"
              type="time"
              required
              value={draft.startTime}
              onChange={(event) => setDraft({ ...draft, startTime: event.target.value })}
            />
            <TextField
              label="Heure de fin"
              type="time"
              required
              value={draft.endTime}
              onChange={(event) => setDraft({ ...draft, endTime: event.target.value })}
            />
          </div>
          <p className="cra-extra-form__duration" aria-live="polite">
            Durée{' '}
            <strong>
              {duration === null
                ? '—'
                : `${(duration / 60).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`}
            </strong>
            {draft.endTime < draft.startTime && <span>Fin le lendemain</span>}
          </p>
          <TextField
            label="Coefficient de majoration"
            type="number"
            min="1"
            max="9.99"
            step="0.01"
            placeholder="Ex. 1,25"
            hint="Facultatif · 1,25 correspond à une majoration de 25 %."
            value={draft.surchargeRate ?? ''}
            onChange={(event) => setDraft({ ...draft, surchargeRate: event.target.value || null })}
          />
          <Textarea
            label="Motif"
            rows={2}
            maxLength={300}
            placeholder="Intervention, livraison, support…"
            value={draft.motive ?? ''}
            onChange={(event) => setDraft({ ...draft, motive: event.target.value })}
          />
        </fieldset>
        {error && (
          <p role="alert" className="cra-notice">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
};
